import { useQueryClient } from '@tanstack/react-query'
import { Check, Upload } from 'lucide-react'
import { type ChangeEvent, useRef } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Marker, MarkerContent, MarkerIcon } from '@/components/ui/marker'
import { Spinner } from '@/components/ui/spinner'
import { CacheKeys } from '@/lib/cache'
import { PARSER_ACCEPTED_FILE_TYPES } from '@/lib/fileUpload'
import { formatCurrency, formatDate } from '@/lib/formatters'
import {
  collectLossRunClaims,
  countLossRunClaims,
  type LossRunClaim,
  LossRunClaimCoverageType,
  useOrgLossRunRequests,
  useUploadLossRunFiles,
} from '@/models/lossRunRequest'
import { SummaryBody } from './SummaryPrimitives'

function parseAmount(value: string | null): number {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

// Loss-run claims carry their own coverage enum, which has an UNKNOWN member the
// policy CoverageType doesn't — so it gets its own labels rather than a cast.
const CLAIM_COVERAGE_LABELS: Record<LossRunClaimCoverageType, string> = {
  [LossRunClaimCoverageType.AUTO_LIABILITY]: 'Auto Liability',
  [LossRunClaimCoverageType.AUTO_PHYSICAL_DAMAGE]: 'Physical Damage',
  [LossRunClaimCoverageType.GENERAL_LIABILITY]: 'General Liability',
  [LossRunClaimCoverageType.MOTOR_TRUCK_CARGO]: 'Cargo',
  [LossRunClaimCoverageType.UNKNOWN]: 'Other',
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-ink-400 text-xs font-semibold tracking-wide uppercase">{children}</span>
}

function ClaimRow({ claim }: { claim: LossRunClaim }) {
  const paid = parseAmount(claim.paidLoss)
  const reserve = parseAmount(claim.reserve)
  const label = CLAIM_COVERAGE_LABELS[claim.coverageType] ?? 'Other'

  return (
    <li className="border-ink-100 flex flex-col gap-1 border-b py-2 last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-foreground text-sm font-medium">{label}</span>
        <span className="text-foreground shrink-0 text-sm">
          {formatCurrency(paid, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
      <div className="text-ink-400 flex items-baseline justify-between gap-2 text-xs">
        <span>
          {claim.incidentDate ? formatDate(claim.incidentDate) : 'Date unknown'}
          {claim.claimCount ? ` · ${claim.claimCount} ${claim.claimCount === 1 ? 'claim' : 'claims'}` : ''}
        </span>
        {reserve > 0 && (
          <span className="shrink-0">
            {formatCurrency(reserve, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} reserved
          </span>
        )}
      </div>
    </li>
  )
}

/** Uploads loss-run documents for the application, then refreshes the list. */
function UploadLossRunsButton({ applicationId, orgId }: { applicationId: string; orgId: string }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const upload = useUploadLossRunFiles(applicationId)

  function onFilesChosen(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    // Clear first so picking the same file twice still fires a change event.
    event.target.value = ''
    if (files.length === 0) return
    upload.mutate(files, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.LOSS_RUN_REQUESTS, orgId] }),
    })
  }

  return (
    <div className="mt-4 flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={PARSER_ACCEPTED_FILE_TYPES}
        className="hidden"
        onChange={onFilesChosen}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        loading={upload.isPending ? 'Uploading…' : undefined}
        onClick={() => inputRef.current?.click()}
      >
        <Upload /> Upload loss runs
      </Button>
      {/* Parsing runs in the background and takes minutes, so confirm the upload
          landed rather than leaving the spinner's disappearance as the only signal. */}
      {upload.isSuccess && (
        <p className="text-brand-700 flex items-start gap-1.5 text-xs">
          <Check className="mt-0.5 size-3.5 shrink-0" />
          Uploaded. Cai is reading them now — claims appear here once they&apos;re processed.
        </p>
      )}
      {upload.isError && (
        <p className="text-destructive text-xs">That upload didn&apos;t go through. Please try again.</p>
      )}
    </div>
  )
}

/**
 * The claims parsed out of the customer's loss runs — this is the claims history, so
 * it leads with the claims themselves rather than the documents they came from (those
 * live in Documents).
 */
export function ClaimsHistorySummary({
  orgId,
  applicationId,
  readonly,
}: {
  orgId: string
  applicationId: string
  /** Shows the claims without the upload, for the review step. */
  readonly?: boolean
}) {
  const { lossRunRequests, isParsing, isLoading } = useOrgLossRunRequests(orgId)

  if (isLoading) {
    return (
      <SummaryBody>
        <p className="text-ink-400 text-sm">Loading claims history…</p>
      </SummaryBody>
    )
  }

  const claims = collectLossRunClaims(lossRunRequests)
  const totalClaims = countLossRunClaims(claims)
  const totalPaid = claims.reduce((sum, claim) => sum + parseAmount(claim.paidLoss), 0)
  // Files are on file but nothing has come back from the parse yet — say so rather than
  // showing the same "no loss runs yet" copy as an account with nothing uploaded.
  const awaitingParse = claims.length === 0 && isParsing

  return (
    <SummaryBody>
      {claims.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-2">
            <SectionLabel>Claims</SectionLabel>
            <Badge variant="secondary">
              {totalClaims} ·{' '}
              {formatCurrency(totalPaid, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} paid
            </Badge>
          </div>
          <ul className="flex flex-col">
            {claims.map((claim) => (
              <ClaimRow key={claim.id} claim={claim} />
            ))}
          </ul>
        </>
      )}

      {awaitingParse && (
        <Marker role="status" className="text-ink-400 items-start text-sm">
          <MarkerIcon className="mt-0.5">
            <Spinner aria-hidden className="size-3.5" />
          </MarkerIcon>
          <MarkerContent>
            Your loss runs are on file — Cai is still reading the claims out of them. They&apos;ll appear here
            once that finishes.
          </MarkerContent>
        </Marker>
      )}

      {claims.length === 0 && !awaitingParse && (
        <p className="text-ink-400 text-sm">
          No claims on file yet. Cai requests loss runs from your current carrier — they usually take a few
          business days.{!readonly && ' You can upload them yourself to speed things up.'}
        </p>
      )}

      {!readonly && <UploadLossRunsButton applicationId={applicationId} orgId={orgId} />}
    </SummaryBody>
  )
}
